/* global gtag */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { showAppAlert } from "../../store/reducers/appreducer";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Typography,
  Container,
  Grid,
  CssBaseline,
  Box,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
} from "@mui/material";
import { styled } from "@mui/system";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import zxcvbn from "zxcvbn";
import loginImage from "../../assets/login.png";
import logoImage from "../../assets/new_logo.png";
import { signupAPI } from "../../services/authService";
const Root = styled("div")(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  overflow: "hidden",
}));

const LeftSide = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(4),
  backgroundColor: "#FFFFFF",
  maxWidth: "50%",
  margin: "40px",
}));

const RightSide = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#F3F7FF",
}));

const ImageContainer = styled("div")(({ theme }) => ({
  width: "514px",
  height: "467px",
  backgroundImage: `url(${loginImage})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundPosition: "center",
  padding: "45.78px 17.89px 45.78px 26.27px",
}));

const FormContainer = styled(Container)(({ theme }) => ({
  maxWidth: "400px",
  margin: "0 auto",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "left",
  // marginBottom: theme.spacing(2),
}));

const Logo = styled("img")(({ theme }) => ({
  width: "200px",
  height: "auto",
  opacity: 1,
  paddingBottom: "8px", // Space inside the border
}));

const LogoText = styled(Typography)(({ theme }) => ({
  marginLeft: "5px",
  marginTop: "20px",
  fontFamily: "Raleway",
  fontSize: "32px",
  fontWeight: 600,
  lineHeight: "39.01px",
  textAlign: "left",
  color: "#1E186B",
  marginBottom: "3px",
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  width: "146px",
  height: "38px",
  top: "212px",
  left: "123px",
  fontFamily: "'Roboto', sans-serif",
  fontSize: "32px",
  fontWeight: 600,
  lineHeight: "37.57px",
  textAlign: "left",
  color: "#5088FF",
  marginBottom: "12px",
}));

const SignUpText = styled(Typography)(({ theme }) => ({
  width: "342px",
  height: "33px",
  top: "258px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: "32.87px",
  textAlign: "left",
  color: "#313D4F",
  marginBottom: "6px",
}));

const DetailsText = styled(Typography)(({ theme }) => ({
  width: "272px",
  height: "19px",
  top: "302px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "16px",
  fontWeight: 500,
  lineHeight: "18.78px",
  textAlign: "left",
  color: "#67768D",
  marginBottom: "24px",
}));

const AgreementText = styled(Typography)(({ theme }) => ({
  width: "571px",
  height: "10px",
  top: "809px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "25.2px",
  textAlign: "left",
  marginTop: theme.spacing(2),
  marginBottom: "24px",
  color: "#262626",
  "& span": {
    color: "#5088FF",
    cursor: "pointer",
    textDecoration: "underline",
  },
}));

const ToSigninText = styled(Typography)(({ theme }) => ({
  width: "571px",
  height: "10px",
  top: "809px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "25.2px",
  textAlign: "left",
  marginTop: theme.spacing(2),
  marginBottom: "24px",
  color: "#262626",
  "& span": {
    color: "#5088FF",
    cursor: "pointer",
    textDecoration: "underline",
  },
}));

const CustomButton = styled(Button)(({ theme }) => ({
  width: "100%",
  height: "56px",
  padding: "8px 8px 8px 8px",
  gap: "8px",
  borderRadius: "8px 8px 8px 8px",
  backgroundColor: "#5088FF",
  color: "#FFFFFF",
  marginTop: theme.spacing(2),
}));

const PasswordStrength = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginTop: theme.spacing(1),
  width: "100%",
}));

const PasswordStrengthBar = styled(LinearProgress)(({ theme, strength }) => ({
  height: "8px",
  borderRadius: "4px",
  backgroundColor: "#e0e0e0",
  flex: 1,
  "& .MuiLinearProgress-bar": {
    backgroundColor:
      strength === 4
        ? "#4caf50"
        : strength === 3
        ? "#8bc34a"
        : strength === 2
        ? "#ffeb3b"
        : strength === 1
        ? "#ff9800"
        : "#f44336",
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "Raleway",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "22.4px",
  textAlign: "left",
  color: "#262626",
  marginBottom: theme.spacing(0.3),
}));

const Signup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [data, setData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "password") {
      const strength = zxcvbn(value);
      setPasswordStrength(strength.score);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateForm();

    if (isValid) {
      setLoading(true);

      if (typeof gtag === "function") {
        gtag("event", "sign_up", {
          event_category: "User Interaction",
          event_label: "Sign Up Button",
        });
      }
      try {
        const userData = {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          password: data.password,
        };

        const result = await signupAPI(userData);

        setLoading(false);

        if (result.error) {
          dispatch(
            showAppAlert({
              show: true,
              alertType: "error",
              alertMessage : result.errorMessage,
            })
          );
          setErrors({ form: result.errorMessage });
        } else {
          setTimeout(() => {
            dispatch(
              showAppAlert({
                show: true,
                alertType: "success",
                alertMessage: "Account created successfully, Click on SignIn",
              })
            );
            navigate("/signin", {
              state: { email: data.email, password: data.password },
            });
          }, 1500);
        }
      } catch (error) {
        setLoading(false);
        setErrors({ form: "An error occurred. Please try again." });
        console.error("Error during signup:", error);
      }
    }
  };

  const validateForm = () => {
    let tempErrors = {};
    tempErrors.first_name = !data.first_name.trim();
    tempErrors.last_name = !data.last_name.trim();
    tempErrors.email = !validateEmail(data.email);
    tempErrors.phone = !validatePhone(data.phone);
    tempErrors.password = !data.password;
    setErrors(tempErrors);
    return !Object.keys(tempErrors).some((key) => tempErrors[key]);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (phone.length === 0) {
      // Allow empty phone numbers
      return true;
    } else {
      // Check if the phone number has exactly 10 digits
      const phoneRegex = /^\d{10}$/;
      return phoneRegex.test(phone);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      {/* Left Side */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: 4,
          bgcolor: "background.paper",
          // maxWidth: '50%',
          maxWidth: { xs: "100%", sm: "50%" },
          margin: { xs: "auto", sm: "unset" },
          m: 5,
        }}
      >
        <Container maxWidth="sm">
          {/* Logo Section */}
          <Box
            onClick={() => window.location.reload()}
            title="Refresh Page"
            sx={{
              cursor: "pointer",
              mb:1,
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <img
              src={logoImage}
              alt="Logo"
              style={{
                width: "auto",
                height: "50px",
                maxWidth: "70%",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Welcome Text */}
          <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#5088FF",
                mb: 2,
                fontFamily: "'Roboto', sans-serif",
                transition: "color 0.3s ease",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                "&:hover": {
                  color: "#007bff",
                },
              }}
            >
              Sign Up
            </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* First Name */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 0.5, color: "#262626" }}>
                  First Name
                  <Box component="span" sx={{ color: "error.main" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  id="firstName"
                  placeholder="Enter First Name"
                  name="first_name"
                  value={data.first_name}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^[a-zA-Z]*$/.test(input)) {
                      handleChange(e);
                    }
                  }}
                  error={errors.first_name}
                  helperText={
                    errors.first_name &&
                    "First name is required and must only contain letters"
                  }
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 0.5, color: "#262626" }}>
                  Last Name
                  <Box component="span" sx={{ color: "error.main" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  id="lastName"
                  placeholder="Enter Last Name"
                  name="last_name"
                  value={data.last_name}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^[a-zA-Z]*$/.test(input)) {
                      handleChange(e);
                    }
                  }}
                  error={errors.last_name}
                  helperText={
                    errors.last_name &&
                    "Last name is required and cannot be blank"
                  }
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 0.5, color: "#262626" }}>
                  Email Address
                  <Box component="span" sx={{ color: "error.main" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  id="email"
                  placeholder="Enter Email"
                  name="email"
                  value={data.email}
                  onChange={handleChange}
                  error={errors.email}
                  helperText={errors.email && "Invalid email address"}
                />
              </Grid>
              {/* Password */}
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 0.5, color: "#262626" }}>
                  Password
                  <Box component="span" sx={{ color: "error.main" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  name="password"
                  placeholder="Enter Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={data.password}
                  onChange={handleChange}
                  error={errors.password}
                  helperText={errors.password && "Password is required"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {/* Phone */}
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 0.5, color: "#262626" }}>
                  Phone Number (Optional)
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  id="phone"
                  placeholder="Enter Phone Number"
                  name="phone"
                  value={data.phone}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d{0,10}$/.test(input)) {
                      handleChange(e);
                    }
                  }}
                  error={errors.phone}
                  helperText={errors.phone && "Phone number must be 10 digits"}
                />
              </Grid>
            </Grid>

            {/* Password Strength */}
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 2,
                  bgcolor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    bgcolor:
                      passwordStrength === 4
                        ? "success.main"
                        : passwordStrength === 3
                        ? "#8bc34a"
                        : passwordStrength === 2
                        ? "warning.light"
                        : passwordStrength === 1
                        ? "warning.main"
                        : "error.main",
                  },
                }}
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {passwordStrength === 4
                  ? "Strong"
                  : passwordStrength === 3
                  ? "Good"
                  : passwordStrength === 2
                  ? "Fair"
                  : passwordStrength === 1
                  ? "Weak"
                  : "Very Weak"}
              </Typography>
            </Box>

            {/* Agreement Text */}
            <Typography variant="body2" sx={{ mt: 2, mb: 3, color: "#262626" }}>
              By Signing up, you agree to our{" "}
              <Box
                component="span"
                onClick={() => alert("Terms of Service")}
                sx={{
                  color: "#5088FF",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Terms of Service
              </Box>{" "}
              and{" "}
              <Box
                component="span"
                onClick={() => alert("Privacy Policy")}
                sx={{
                  color: "#5088FF",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Privacy Policy
              </Box>
            </Typography>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                height: 56,
                borderRadius: 1,
                bgcolor: "#5088FF",
                "&:hover": {
                  bgcolor: "#4070DD",
                },
              }}
            >
              {loading ? "Creating..." : "Let's go!"}
            </Button>
            <Stack
              direction="row"
              spacing={0}
              justifyContent="center"
              alignItems="center"
              mt={3}
            >
              <Typography variant="body2">Already have an account?</Typography>
              <Link
                to="/signin"
                style={{
                  textDecoration: "none",
                  color: "#5088FF",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    textDecoration: "none",
                    color: "primary.main",
                    fontWeight: 600,
                    "&:hover": {
                      color: "#007bff",
                    },
                  }}
                >
                  Sign In
                </Typography>
              </Link>
            </Stack>
          </form>
        </Container>
      </Box>

      {/* Right Side */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#F3F7FF",
          maxWidth: { xs: "0", sm: "50%" },
          display: { xs: "none", sm: "flex" },
        }}
      >
        <Box
          sx={{
            width: 514,
            height: 467,
            backgroundImage: `url(${loginImage})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            p: "45.78px 17.89px 45.78px 26.27px",
          }}
        />
      </Box>
    </Box>
  );
};

export default Signup;
